import React, { useEffect, useState } from 'react';
import SeparationLine from '../SeparationLine/Separation';
import '../AlarmsModal/AlarmModal';
import './Modal.css';

type Alarm = {
    id: number;
    severity: string;
    alarmName: string;
    dataPoint: string;
    condition: string;
    value: string;
    status: string;
}


interface AlarmProps {
    isEditSelected: boolean;
    isEnabled: boolean;
    isLoading: boolean;
    addNewAlarm: () => Promise<void>;
    updateAlarm: () => Promise<void>;
    newAlarm: Alarm;
    handleAlarmValueChange: (key: any, value: any) => void
    setIsEnabled: React.Dispatch<React.SetStateAction<boolean>>
}

const AlarmFormMobile: React.FC<AlarmProps> = ({
    isEditSelected,
    isLoading,
    isEnabled,
    newAlarm,
    addNewAlarm,
    updateAlarm,
    handleAlarmValueChange,
    setIsEnabled
}) => {

    const handleToggleChange = () => {
        setIsEnabled(!isEnabled);
        handleAlarmValueChange("status", !isEnabled ? "Enabled" : "Disabled");
    };

    const resetForm = () => {
        handleAlarmValueChange("reset", "");
    };
    const isFormInvalid = Object.values(newAlarm).some(value => {
        if (typeof value === "string") return value.trim() === ""; // Check for empty strings
        return value === null || value === undefined; // Check for null/undefined
    });

    return (
        <div className="mx-auto" style={{ width: "90%" }}>
            <div className="bg-darkcolor-600 rounded-lg">
                <div className="text-left">
                    <h2 className="pl-6 py-3.5 text-sm text-gray-300 text-left">
                        {isEditSelected ? "Update alarm setup" : "Create an alarm"}
                    </h2>
                </div>
                <SeparationLine />
                <div className="space-y-2 px-6">
                    <div>
                        <label htmlFor="alarmName" className="modal-label mb-6">
                            Alarm name
                        </label>
                        <input
                            id="alarmName"
                            type="text"
                            className="w-full p-2 modal-button"
                            value={newAlarm.alarmName}
                            onChange={(e) => handleAlarmValueChange("alarmName", e.target.value)}
                        />
                    </div>
                    <div>
                        <label htmlFor="dataPoint" className="modal-label">
                            Data point
                        </label>
                        <select
                            id="dataPoint"
                            className="w-full p-2 modal-button"
                            value={newAlarm.dataPoint}
                            onChange={(e) => handleAlarmValueChange("dataPoint", e.target.value)}
                        >
                            <option value="Option 1">Option 1</option>
                            <option value="Option 2">Option 2</option>
                            <option value="Option 3">Option 3</option>
                        </select>
                    </div>
                    <div>
                        <label htmlFor="condition" className="modal-label">
                            Condition
                        </label>
                        <select
                            id="condition"
                            className="w-full p-2 modal-button"
                            value={newAlarm.condition}
                            onChange={(e) => handleAlarmValueChange("condition", e.target.value)}
                        >
                            <option value="Option 1">Option 1</option>
                            <option value="Option 2">Option 2</option>
                            <option value="Option 3">Option 3</option>
                        </select>
                    </div>
                    <div>
                        <label htmlFor="value" className="modal-label">
                            Value
                        </label>
                        <input
                            id="value"
                            type="text"
                            className="w-full p-2 modal-button"
                            value={newAlarm.value}
                            onChange={(e) => handleAlarmValueChange("value", e.target.value)}
                        />
                    </div>
                    {/* Severity Section */}
                    <div className="mb-15">
                        <label className="block text-sm font-medium text-gray-300">Severity</label>
                        <div className="flex space-x-2 mt-2">
                            {/* Critical */}
                            <div className="flex items-center">
                                <input
                                    type="radio"
                                    id="critical"
                                    name="severity"
                                    checked={newAlarm.severity === "critical"}
                                    onChange={() => handleAlarmValueChange("severity", "critical")}
                                    className="appearance-none w-4 h-4 rounded-full border"
                                    style={{
                                        border: "1px solid var(--Error-Red-700, #B42318)",
                                        backgroundColor:
                                            newAlarm.severity === "critical"
                                                ? "var(--Error-Red-700, #B42318)"
                                                : "transparent",
                                    }}
                                />
                                <label htmlFor="critical" className="ml-2 text-sm text-gray-300">
                                    Critical
                                </label>
                            </div>
                            {/* High */}
                            <div className="flex items-center">
                                <input
                                    type="radio"
                                    id="high"
                                    name="severity"
                                    checked={newAlarm.severity === "high"}
                                    onChange={() => handleAlarmValueChange("severity", "high")}
                                    className="appearance-none w-4 h-4 rounded-full border"
                                    style={{
                                        border: "1px solid var(--Colors-Red, #FF3B30)",
                                        backgroundColor:
                                            newAlarm.severity === "high"
                                                ? "var(--Colors-Red, #FF3B30)"
                                                : "transparent",
                                    }}
                                />
                                <label htmlFor="high" className="ml-2 text-sm text-gray-300">
                                    High
                                </label>
                            </div>
                            {/* Medium */}
                            <div className="flex items-center">
                                <input
                                    type="radio"
                                    id="medium"
                                    name="severity"
                                    checked={newAlarm.severity === "medium"}
                                    onChange={() => handleAlarmValueChange("severity", "medium")}
                                    className="appearance-none w-4 h-4 rounded-full border"
                                    style={{
                                        border: "1px solid var(--Secondary-Orange-400, #FB743C)",
                                        backgroundColor:
                                            newAlarm.severity === "medium"
                                                ? "var(--Secondary-Orange-400, #FB743C)"
                                                : "transparent",
                                    }}
                                />
                                <label htmlFor="medium" className="ml-2 text-sm text-gray-300">
                                    Medium
                                </label>
                            </div>
                            {/* Low */}
                            <div className="flex items-center">
                                <input
                                    type="radio"
                                    id="low"
                                    name="severity"
                                    checked={newAlarm.severity === "low"}
                                    onChange={() => handleAlarmValueChange("severity", "low")}
                                    className="appearance-none w-4 h-4 rounded-full border"
                                    style={{
                                        border: "1px solid var(--Warning-Yellow-400, #FDB022)",
                                        backgroundColor:
                                            newAlarm.severity === "low"
                                                ? "var(--Warning-Yellow-400, #FDB022)"
                                                : "transparent",
                                    }}
                                />
                                <label htmlFor="low" className="ml-2 text-sm text-gray-300">
                                    Low
                                </label>
                            </div>
                        </div>
                    </div>

                    <div className="flex items-center" style={{ marginTop: "10px" }}>
                        <label className="switch">
                            <input
                                type="checkbox"
                                checked={isEnabled}
                                onChange={handleToggleChange}
                            />
                            <span className="slider"></span>
                        </label>
                        <label
                            style={{
                                marginLeft: "12px",
                                verticalAlign: "3px",
                                transition: "color 0.4s",
                            }}
                            className="text-sm text-gray-300"
                        >
                            Enabled
                        </label>
                    </div>
                </div>
                <div className="mt-6 flex justify-end space-x-2">
                    <button
                        className={`px-4 py-2 rounded-md ${isLoading
                            ? "bg-gray-400 text-gray-200 cursor-not-allowed"
                            : "bg-red-600 text-white hover:bg-red-700"
                            }`}
                        onClick={resetForm}
                        disabled={isLoading}
                        style={{ color: isLoading ? "grey" : "" }}
                    >
                        Reset
                    </button>
                    <button
                        className={`px-4 py-2 rounded-md ${isLoading || isFormInvalid
                            ? "bg-gray-400 text-gray-200 cursor-not-allowed"
                            : "bg-green-600 text-white hover:bg-green-700"
                            }`}
                        onClick={isEditSelected ? updateAlarm : addNewAlarm}
                        disabled={isLoading || isFormInvalid}
                        style={{ color: isLoading || isFormInvalid ? "grey" : "" }}
                    >
                        {isEditSelected ? "Update" : "Add"}
                    </button>
                </div>


            </div>
        </div>
    );
};


export default AlarmFormMobile;
